<script setup lang="ts">
/**
 * Country / City field of the Search region dialog — label over the value,
 * underline (`Input`, 316:4421). Desktop opens the list in a popover under the
 * field (D 11); compact asks the dialog for a second sheet instead (M 11).
 */
import { ref, watch } from 'vue'
import Popover from 'primevue/popover'
import { FairIcon } from '@/ui/primitives/FairIcon'
import FairProfileV2RegionList from './FairProfileV2RegionList.vue'
import type { FairProfileV2RegionOption } from './fair-profile-v2-settings.data'

interface FairProfileV2RegionFieldProps {
  label: string
  /** Selected option label; the placeholder shows when empty. */
  value?: string
  placeholder: string
  disabled?: boolean
  compact?: boolean
  options: FairProfileV2RegionOption[]
  selectedId?: string | null
  loading?: boolean
  error?: boolean
  emptyText: string
  /** People dialog (`05`): `Relation: Sister` on one line with a chevron, no floating label. */
  inline?: boolean
  searchable?: boolean
}

const props = withDefaults(defineProps<FairProfileV2RegionFieldProps>(), {
  value: '',
  disabled: false,
  compact: false,
  selectedId: null,
  loading: false,
  error: false,
  inline: false,
  searchable: true,
})

const emit = defineEmits<{
  select: [option: FairProfileV2RegionOption]
  retry: []
  /** Compact only: open the list sheet. */
  open: []
}>()

const triggerRef = ref<HTMLButtonElement | null>(null)
const popoverRef = ref<InstanceType<typeof Popover> | null>(null)
const isOpen = ref(false)
const menuWidth = ref('auto')

// Crossing 1024 unmounts the popover without its `hide` event.
watch(
  () => props.compact,
  () => {
    isOpen.value = false
  },
)

function toggle(event: MouseEvent) {
  if (props.compact) {
    emit('open')
    return
  }
  menuWidth.value = `${triggerRef.value?.offsetWidth ?? 0}px`
  popoverRef.value?.toggle(event, triggerRef.value)
}

function pick(option: FairProfileV2RegionOption) {
  popoverRef.value?.hide()
  emit('select', option)
}
</script>

<template>
  <div
    class="fair-profile-v2-region-field"
    :class="{ 'fair-profile-v2-region-field--open': isOpen }"
  >
    <button
      ref="triggerRef"
      type="button"
      class="fair-profile-v2-region-field__trigger"
      :class="{ 'fair-profile-v2-region-field__trigger--inline': inline }"
      :disabled="disabled"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <template v-if="inline">
        <span class="fair-profile-v2-region-field__value fair-profile-v2-region-field__value--placeholder">
          {{ value ? `${label}: ${value}` : placeholder }}
        </span>
        <FairIcon
          class="fair-profile-v2-region-field__chevron"
          name="chevron-down"
          :size="16"
        />
      </template>
      <template v-else>
        <span
          v-if="value"
          class="fair-profile-v2-region-field__label"
        >{{ label }}</span>
        <span
          class="fair-profile-v2-region-field__value"
          :class="{ 'fair-profile-v2-region-field__value--placeholder': !value }"
        >{{ value || placeholder }}</span>
      </template>
    </button>

    <Popover
      v-if="!compact"
      ref="popoverRef"
      class="fair-profile-v2-region-menu"
      :style="{ width: menuWidth }"
      @show="isOpen = true"
      @hide="isOpen = false"
    >
      <FairProfileV2RegionList
        :options="options"
        :selected-id="selectedId"
        :loading="loading"
        :error="error"
        :empty-text="emptyText"
        :searchable="searchable"
        :radios="!searchable"
        @select="pick"
        @retry="emit('retry')"
      />
    </Popover>
  </div>
</template>
